import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { Icon } from "../app/Icon";
import { Avatar } from "../components/Avatar";
import { PROFILE_TYPE_LABELS, setDefaultProfile, type Profile, type ProfileType } from "../lib/profiles";
import { getVerificationStatus, type VerificationStatus } from "../lib/verification";

const tileTone: Record<ProfileType, string> = {
  driver: "bg-verified-bg text-verified",
  company: "bg-info/15 text-info",
  recruiter: "bg-accent/15 text-accent-strong",
  broker_dispatcher: "bg-surface-2 text-muted",
};

export function MyProfiles() {
  const { account } = useAuth();
  const { profiles, active, setActive, refresh } = useProfiles();
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    getVerificationStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  const verifiedProfiles = new Set(status?.credentials.filter((c) => c.status === "verified").map((c) => c.profile_id));
  const isVerified = (id: string) => Boolean(account?.identity_verified) && verifiedProfiles.has(id);

  async function makeDefault(p: Profile) {
    setBusy(p.id);
    try {
      await setDefaultProfile(p.id);
      setActive(p.id);
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        title="My profiles"
        subtitle="Manage and switch between your profiles."
        actions={<Link to="/profiles/new"><Button><span className="flex items-center gap-1.5"><Icon name="add" className="h-4 w-4" /> New profile</span></Button></Link>}
      />

      <div className="grid gap-4 md:grid-cols-2">
        {profiles.map((p) => (
          <div key={p.id} className={`grid gap-4 rounded-card border bg-surface p-5 ${active?.id === p.id ? "border-accent" : "border-line"}`}>
            <div className="flex items-start gap-4">
              <Avatar src={p.image_url} name={p.display_name ?? PROFILE_TYPE_LABELS[p.type]} className="h-16 w-16 rounded-lg text-2xl" tone={tileTone[p.type]} />
              <div className="grid min-w-0 gap-1">
                <h2 className="truncate font-display text-lg font-black tracking-tight">{p.display_name ?? PROFILE_TYPE_LABELS[p.type]}</h2>
                <p className="font-mono text-xs">
                  <span className="text-muted">{PROFILE_TYPE_LABELS[p.type]}</span>
                  {" · "}
                  {isVerified(p.id) ? <span className="text-verified">VERIFIED ✓</span> : <span className="text-info">NEEDS REVIEW</span>}
                </p>
                {p.is_default && (
                  <span className="w-max rounded-md border border-line px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-muted">Default</span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-line pt-4">
              {!p.is_default && (
                <Button variant="ghost" disabled={busy === p.id} onClick={() => makeDefault(p)}>
                  <span className="flex items-center gap-1.5"><Icon name="star" className="h-4 w-4" /> Set default</span>
                </Button>
              )}
              <Link to={`/profiles/${p.id}/edit`}>
                <Button variant="ghost"><span className="flex items-center gap-1.5"><Icon name="pencil" className="h-4 w-4" /> Edit</span></Button>
              </Link>
              <Link to={`/p/${p.id}`}>
                <Button variant="ghost"><span className="flex items-center gap-1.5"><Icon name="privacy" className="h-4 w-4" /> View</span></Button>
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
